import { Component, Inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CeoDto } from 'src/app/data-transfer/entities/doc-entities/ceo-dto';
import { SigneeDto } from 'src/app/data-transfer/entities/doc-entities/signee-dto';
import { PathResolveService } from 'src/app/services/path-resolve.service';

export interface CreateDocSignersDialogData {
  managingDirectors: CeoDto[];
  signers: SigneeDto[];
}

type PersonSelection<T> = T | string | null;

@Component({
  selector: 'app-create-doc-signers-dialog',
  templateUrl: './create-doc-signers-dialog.component.html',
  styleUrls: ['./create-doc-signers-dialog.component.scss'],
})
export class CreateDocSignersDialogComponent {
  readonly managingDirectors: CeoDto[];
  readonly signers: SigneeDto[];

  form: FormGroup<{
    managingDirector: FormControl<PersonSelection<CeoDto>>;
    signer: FormControl<PersonSelection<SigneeDto>>;
    hasOtherSigner: FormControl<boolean>;
  }>;

  constructor(
    private router: Router,
    private dialogRef: MatDialogRef<CreateDocSignersDialogComponent>,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) data: CreateDocSignersDialogData
  ) {
    this.managingDirectors = data.managingDirectors;
    this.signers = data.signers;
    this.form = this.fb.group({
      managingDirector: new FormControl<PersonSelection<CeoDto>>(
        data.managingDirectors.length === 1 ? data.managingDirectors[0] : null,
        this.personSelectedValidator()
      ),
      signer: new FormControl<PersonSelection<SigneeDto>>(data.signers.length === 1 ? data.signers[0] : null, this.optionalPersonSelectedValidator()),
      hasOtherSigner: new FormControl<boolean>(false, { nonNullable: true }),
    });

    this.form.controls.hasOtherSigner.valueChanges.subscribe(() => this.form.controls.signer.updateValueAndValidity());
  }

  private personSelectedValidator(): ValidatorFn {
    return (control): ValidationErrors | null => (control.value && typeof control.value === 'object' ? null : { required: true });
  }

  private optionalPersonSelectedValidator(): ValidatorFn {
    return (control): ValidationErrors | null => {
      if (!this.form?.controls.hasOtherSigner.value) return null;
      return control.value && typeof control.value === 'object' ? null : { required: true };
    };
  }

  get filteredManagingDirectors(): CeoDto[] {
    return this.filterPersons(this.managingDirectors, this.form.controls.managingDirector.value);
  }

  get filteredSigners(): SigneeDto[] {
    return this.filterPersons(this.signers, this.form.controls.signer.value);
  }

  private filterPersons<T extends CeoDto>(list: T[], value: PersonSelection<T>): T[] {
    if (typeof value !== 'string') return list;
    const term = value.trim().toLowerCase();
    return term ? list.filter(p => `${p.firstName} ${p.lastName} ${p.email}`.toLowerCase().includes(term)) : list;
  }

  displayPerson = (value: PersonSelection<CeoDto | SigneeDto>): string => {
    return typeof value === 'string' ? value : '';
  };

  clickOrganisationSettings(): void {
    this.onCancel();
    const path = PathResolveService.getOrganisationSettingsPath(true);
    this.router.navigate([path]);
  }

  get selectedManagingDirector(): CeoDto | undefined {
    const value = this.form.controls.managingDirector.value;
    return value && typeof value === 'object' ? value : undefined;
  }

  get selectedSigner(): SigneeDto | undefined {
    const value = this.form.controls.signer.value;
    return value && typeof value === 'object' ? value : undefined;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onContinue(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    this.dialogRef.close({
      confirmed: true,
      ceoId: this.selectedManagingDirector?.personProfileId,
      signeeId: this.form.value.hasOtherSigner ? (this.selectedSigner?.personProfileId ?? null) : null,
    });
  }
}
